import React, { useState, useEffect } from 'react';
import { CheckCircle, Copy, Share2, Calendar, FileBarChart, FlaskConical, Loader2 } from 'lucide-react';
import Button from '../../Button';
import ShareModal from '../../shared/ShareModal';
import HelpTrigger from '../../../help/components/HelpTrigger';
import toast from 'react-hot-toast';
import { AnalyticsIcon } from '../../icons';

interface Props {
  itemId?: string;
  collectorUrl?: string;
  onSaveDraft: () => void;
  onLaunchNow: (dates?: { effective_start?: string; effective_end?: string }) => void;
  onCreatePublicReport?: () => void;
  onLaunchTest?: () => void;
  testLinkUrl?: string;
  creatingTestLink?: boolean;
  saving: boolean;
  launching: boolean;
  launched: boolean;
  shareResultsLink?: boolean;
}

const WizardStep6Launch: React.FC<Props> = ({
  itemId,
  collectorUrl,
  onSaveDraft,
  onLaunchNow,
  onCreatePublicReport,
  onLaunchTest,
  testLinkUrl,
  creatingTestLink = false,
  saving,
  launching,
  launched,
  shareResultsLink
}) => {
  const [copied, setCopied] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showTestShareModal, setShowTestShareModal] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Open the share modal as soon as the parent mints the test link
  useEffect(() => {
    if (testLinkUrl) {
      setShowTestShareModal(true);
    }
  }, [testLinkUrl]);

  const copyLink = () => {
    if (collectorUrl) {
      navigator.clipboard.writeText(collectorUrl);
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (launched && collectorUrl) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/20 mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
            Outreach Launched Successfully!
          </h2>
          <p className="text-text-secondary dark:text-text-dark-secondary">
            Your outreach is now live and ready to share
          </p>
        </div>

        {/* Public Link */}
        <div className="card p-6">
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Your Public Link
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              value={collectorUrl}
              readOnly
              className="input flex-1"
            />
            <Button variant="secondary" onClick={copyLink}>
              {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            </Button>
            <Button variant="secondary" onClick={() => setShowShareModal(true)}>
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button variant="primary" fullWidth onClick={() => window.location.href = `/outreach/analytics/${itemId}`}>
            <AnalyticsIcon className="w-4 h-4 mr-2" />
            View Analytics
          </Button>
          <Button variant="secondary" fullWidth onClick={() => setShowShareModal(true)}>
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {showShareModal && (
          <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            shareUrl={collectorUrl}
            title="Share Outreach"
            emailSubject="Check out this outreach"
          />
        )}
      </div>
    );
  }

  const handleLaunch = () => {
    const dates: { effective_start?: string; effective_end?: string } = {};
    
    if (startDate) {
      dates.effective_start = new Date(startDate).toISOString();
    }
    if (endDate) {
      dates.effective_end = new Date(endDate).toISOString();
    }
    
    onLaunchNow(Object.keys(dates).length > 0 ? dates : undefined);
  };

  const isFutureDate = startDate && new Date(startDate) > new Date();

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary">
            Ready to Launch!
          </h2>
          <HelpTrigger contentKey="outreach-test-mode" size="md" />
        </div>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Choose how you&apos;d like to proceed
        </p>
      </div>

      {/* Schedule Section (Optional) */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-text-primary dark:text-text-dark-primary">
              Schedule (Optional)
            </h3>
          </div>
          <button
            onClick={() => setShowSchedule(!showSchedule)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {showSchedule ? 'Hide' : 'Set dates'}
          </button>
        </div>

        {showSchedule && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  Start Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input w-full"
                />
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                  Leave empty to start immediately
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                  End Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || undefined}
                  className="input w-full"
                />
                <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                  Leave empty for no end date
                </p>
              </div>
            </div>

            {isFutureDate && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  ℹ️ <strong>Scheduled Launch:</strong> Your outreach will remain in draft status until{' '}
                  {new Date(startDate).toLocaleString()}, at which point it will automatically become active.
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Options — a vertical list. As columns, four of these left each one a
          narrow strip where every label and CTA wrapped. Full width lets the
          title, description and call to action sit on one readable line each. */}
      <div className="space-y-3">
        <button
          onClick={onSaveDraft}
          disabled={saving || launching}
          className="card w-full p-5 text-left flex items-center gap-4 hover:border-primary-500 dark:hover:border-primary-500 transition-all disabled:opacity-60"
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-1">
              Save as Draft
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              Save your outreach as a draft. You can launch it later from your outreach list.
            </p>
          </div>
          <div className="text-sm text-primary-600 dark:text-primary-400 whitespace-nowrap flex-shrink-0">
            Save &amp; Return →
          </div>
        </button>

        {onLaunchTest && (
          <button
            onClick={onLaunchTest}
            disabled={saving || launching || creatingTestLink}
            className="card w-full p-5 text-left flex items-center gap-4 bg-amber-50 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800 hover:border-amber-500 dark:hover:border-amber-500 transition-all disabled:opacity-60"
          >
            {creatingTestLink
              ? <Loader2 className="w-5 h-5 animate-spin text-amber-600 dark:text-amber-400 flex-shrink-0" />
              : <FlaskConical className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary">
                  Launch Test
                </h3>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                  Recommended
                </span>
              </div>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Save as draft and get a test link that runs the real flow. Test responses are never counted, and you&apos;ll be asked to keep or delete them at launch.
              </p>
            </div>
            <div className="text-sm font-medium text-amber-600 dark:text-amber-400 whitespace-nowrap flex-shrink-0">
              {creatingTestLink ? 'Creating test link…' : 'Save & Get Test Link →'}
            </div>
          </button>
        )}

        <button
          onClick={handleLaunch}
          disabled={saving || launching}
          className="card w-full p-5 text-left flex items-center gap-4 bg-primary-50 dark:bg-primary-900/10 border-primary-200 dark:border-primary-800 hover:border-primary-500 dark:hover:border-primary-500 transition-all disabled:opacity-60"
        >
          <div className="flex-1 min-w-0">
            <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-1">
              {isFutureDate ? 'Schedule Launch' : 'Launch Now'}
            </h3>
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {isFutureDate
                ? 'Schedule your outreach to activate automatically at the specified start date.'
                : 'Activate your outreach and generate a shareable link immediately. Perfect if you\'re ready to start collecting responses.'}
            </p>
          </div>
          <div className="text-sm font-medium text-primary-600 dark:text-primary-400 whitespace-nowrap flex-shrink-0">
            {isFutureDate ? 'Schedule →' : 'Launch & Share →'}
          </div>
        </button>

        {shareResultsLink && onCreatePublicReport && (
          <button
            onClick={onCreatePublicReport}
            disabled={saving || launching}
            className="card w-full p-5 text-left flex items-center gap-4 bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800 hover:border-green-500 dark:hover:border-green-500 transition-all disabled:opacity-60"
          >
            <FileBarChart className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <h3 className="text-base font-semibold text-text-primary dark:text-text-dark-primary mb-1">
                Create Public Report
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
                Save as draft and set up the public report that respondents will see when they click &ldquo;View Real-Time Results.&rdquo;
              </p>
            </div>
            <div className="text-sm font-medium text-green-600 dark:text-green-400 whitespace-nowrap flex-shrink-0">
              Save &amp; Create Report →
            </div>
          </button>
        )}
      </div>

      {/* Test link share — opens once the parent mints the link */}
      {testLinkUrl && (
        <ShareModal
          isOpen={showTestShareModal}
          onClose={() => setShowTestShareModal(false)}
          shareUrl={testLinkUrl}
          title="Test Link"
          shareText="Test this outreach before launch"
          emailSubject="Please test this outreach"
        />
      )}
    </div>
  );
};

export default WizardStep6Launch;

